import posthog from 'posthog-js'

export interface InitPostHogOptions {
  /** Project API key (public). Leave empty to skip init (e.g. local dev). */
  key: string | undefined
  /** PostHog host. Usually 'https://us.i.posthog.com' or self-hosted URL. */
  host?: string
  /**
   * App tag. Every captured event will include `app: <value>` as a property so
   * a single PostHog project can serve admin/OS/academy and dashboards can
   * filter per-app.
   */
  app: 'admin' | 'platform' | 'academy' | (string & {})
  /** Environment tag — 'production' / 'preview' / 'development'. */
  environment: string
  /**
   * Disable autocapture? Defaults to `false` (autocapture on). Set `true` for
   * apps with heavy form input where autocapture noise isn't worth it.
   */
  disableAutocapture?: boolean
}

const DEFAULT_HOST = 'https://us.i.posthog.com'

/**
 * Boot PostHog for an L3 app. Safe to call with an empty key — PostHog stays
 * uninitialized and the identify/capture helpers below become no-ops.
 *
 * Events carry `app` and `environment` as super-properties so one PostHog
 * project can serve the whole ecosystem with per-app dashboards.
 */
export function initPostHog(opts: InitPostHogOptions): void {
  if (!opts.key) {
    console.info('[observability] PostHog key not provided — skipping init')
    return
  }

  posthog.init(opts.key, {
    api_host: opts.host ?? DEFAULT_HOST,
    capture_pageview: true,
    autocapture: !opts.disableAutocapture,
    // Clicked content can be sensitive (e.g. transcripts) — never ship element text/attributes.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Replay has its own masking config and nothing uses it — keep it off.
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      ph.register({ app: opts.app, environment: opts.environment })
    },
  })
}

/**
 * Identify the current user in PostHog. Called after Clerk sign-in. Safe to
 * call when PostHog isn't initialized.
 */
export function identifyPostHogUser(user: {
  id: string
  email?: string
  role?: string
}): void {
  if (!posthog.__loaded) return
  posthog.identify(user.id, {
    email: user.email,
    role: user.role,
  })
}

/**
 * Reset PostHog identity on sign-out. Safe to call when PostHog isn't loaded.
 */
export function resetPostHog(): void {
  if (!posthog.__loaded) return
  posthog.reset()
}

export { posthog }
